import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import RequiredIndicator from '@/components/common/RequiredIndicator';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useContact from '@/hooks/useContact';
import { ContactProps } from '@/models/Contact';
import ContactCard from './ContactCard';
import DuplicateContactPromptModal from './DuplicateContactPromptModal';
import ContactModal from '@/components/application_components/question_types/ContactModal';
import { QuestionProps } from '@/models/Question';
import {
  findExistingNameEmailMatch,
  getTrueDuplicateFingerprint,
} from '@/utils/contactDuplicateUtils';
import { buildContactSelectOptions, getPreferredContact } from '@/utils/contactSelectUtils';
import { stringHasValue } from '@/utils/stringUtil';
import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';

const TREE_BOARD_CONTACT_TYPE_CHAIRPERSON = 6;
const TREE_BOARD_CONTACT_TYPE_MEMBER = 7;

function Contact({
  question,
  order,
  initialContact,
  onSave,
  onDelete,
}: {
  question: QuestionProps;
  order: number;
  initialContact: ContactProps;
  onSave: (contact: ContactProps) => Promise<boolean>;
  onDelete: (contactId: number) => Promise<boolean>;
}) {
  const contactConfig = question.contactConfig!;
  const { departmentTypes } = useContext(ConfigContext);

  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { setAlert } = useContext(SnackbarContext);
  const {
    currentContacts: applicationContactList,
    updateContacts: setApplicationContactList,
    partnerInfo,
    currentApplication,
    canEdit,
    isPreviewMode,
    saveApplicationAnswer,
  } = useContext(CurrentApplicationContext);

  const { locale: localeFromContext, translatedText } = useContext(LocaleContext);
  const locale = localeFromContext!;

  const { getContact, saveContact, isContactValid } = useContact();

  const [contactError, setContactError] = useState(false);

  const [loading, setLoading] = useState(false);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [duplicateContactPromptOpen, setDuplicateContactPromptOpen] = useState(false);
  const [duplicateContactPromptLoading, setDuplicateContactPromptLoading] = useState(false);
  const [pendingDuplicateContact, setPendingDuplicateContact] = useState<{
    draftContact: ContactProps;
    existingContact: ContactProps;
  } | null>(null);
  const [contactInfo, setContactInfo] = useState(initialContact);

  const hasRelationshipForType = (contact: ContactProps, contactTypeId: number): boolean => {
    return (contact.contactRelationships ?? []).some(
      (relationship) =>
        relationship.applicationId === currentApplication.id &&
        relationship.contactTypeId === contactTypeId
    );
  };

  const isTreeBoardType =
    contactConfig.id === TREE_BOARD_CONTACT_TYPE_CHAIRPERSON ||
    contactConfig.id === TREE_BOARD_CONTACT_TYPE_MEMBER;

  useEffect(() => {
    if (applicationContactList == null || applicationContactList.length < 2) {
      return;
    }

    const contactsWithIds = applicationContactList.filter((contact) => contact.id != null);
    if (contactsWithIds.length < 2) {
      return;
    }

    const preferredByFingerprint = new Map<string, ContactProps>();
    for (const contact of contactsWithIds) {
      const fingerprint = getTrueDuplicateFingerprint(contact);
      const existing = preferredByFingerprint.get(fingerprint);
      preferredByFingerprint.set(
        fingerprint,
        existing == null ? contact : getPreferredContact(existing, contact)
      );
    }

    if (preferredByFingerprint.size === contactsWithIds.length) {
      return;
    }

    const seenFingerprints = new Set<string>();
    const dedupedContacts: ContactProps[] = [];
    for (const contact of applicationContactList) {
      if (contact.id == null) {
        dedupedContacts.push(contact);
        continue;
      }

      const fingerprint = getTrueDuplicateFingerprint(contact);
      if (seenFingerprints.has(fingerprint)) {
        continue;
      }

      seenFingerprints.add(fingerprint);
      dedupedContacts.push(preferredByFingerprint.get(fingerprint) ?? contact);
    }

    setApplicationContactList(dedupedContacts);
  }, [applicationContactList, setApplicationContactList]);

  useEffect(() => {
    setContactInfo(initialContact);
  }, [initialContact]);

  function syncSavedContactAcrossReferences(savedContact: ContactProps) {
    if (savedContact.id == null) {
      return;
    }

    if (applicationContactList == null || applicationContactList.length === 0) {
      return;
    }

    const existingIndex = applicationContactList.findIndex((c) => c.id === savedContact.id);
    if (existingIndex < 0) {
      return;
    }

    const existingContact = applicationContactList[existingIndex];
    const updatedContact: ContactProps = {
      ...existingContact,
      ...savedContact,
      // Preserve the full relationship set already held in currentContacts.
      contactRelationships:
        existingContact.contactRelationships ?? savedContact.contactRelationships ?? [],
    };

    const updatedContacts = [...applicationContactList];
    updatedContacts[existingIndex] = updatedContact;
    setApplicationContactList(updatedContacts);
  }

  async function saveContactRelationship(contact: ContactProps): Promise<boolean> {
    if (contact.id == null) {
      return false;
    }

    setLoading(true);
    try {
      return await onSave(contact);
    } finally {
      setLoading(false);
    }
  }

  function openBlankContactModal() {
    setContactInfo({
      city: '',
      contactRelationships: [
        {
          applicationId: currentApplication.id,
          contactTypeId: contactConfig.id,
        },
      ],
    });
    setOpenContactModal(true);
  }

  async function handleContactSelect(event: SelectChangeEvent) {
    if (event.target.value === 'new') {
      openBlankContactModal();
    } else {
      const newContactId = Number(event.target.value);

      const currentContactRelationships: ContactProps[] = isTreeBoardType
        ? // if contactType is Community Tree Board Chairperson or Member,
          // we want both types in relationships list
          (applicationContactList ?? []).filter(
            (contact) =>
              hasRelationshipForType(contact, TREE_BOARD_CONTACT_TYPE_CHAIRPERSON) ||
              hasRelationshipForType(contact, TREE_BOARD_CONTACT_TYPE_MEMBER)
          )
        : (applicationContactList ?? []).filter((contact) =>
            hasRelationshipForType(contact, contactConfig.id)
          );

      const crIndex = currentContactRelationships.findIndex(
        (currentContactRelationship) => currentContactRelationship.id === newContactId
      );

      const matchingContactRelationship =
        crIndex >= 0 ? currentContactRelationships[crIndex] : null;

      let currentlySelectedContact = null;

      if (crIndex < 0) {
        setLoading(true);
        try {
          currentlySelectedContact = await getContact(
            newContactId,
            isPreviewMode,
            applicationContactList
          );
          if (currentlySelectedContact?.id !== null && currentlySelectedContact?.id !== undefined) {
            // In multi-instance mode, changing an existing row should replace that row's
            // relationship, not append a second relationship.
            if (
              contactConfig.maxInstances > 1 &&
              contactInfo.id != null &&
              contactInfo.id !== currentlySelectedContact.id
            ) {
              const removedCurrent = await onDelete(contactInfo.id);
              if (!removedCurrent) {
                currentlySelectedContact = null;
              }
            }

            if (currentlySelectedContact != null) {
              const relationshipSaved = await saveContactRelationship(currentlySelectedContact);
              if (!relationshipSaved) {
                currentlySelectedContact = null;
              }
            }
          }
        } finally {
          setLoading(false);
        }
      } else if (
        contactConfig.maxInstances === 1 &&
        matchingContactRelationship !== null &&
        hasRelationshipForType(matchingContactRelationship, contactConfig.id)
      ) {
        setLoading(true);
        try {
          currentlySelectedContact = await getContact(
            newContactId,
            isPreviewMode,
            applicationContactList
          );
        } finally {
          setLoading(false);
        }
        setAlert({
          message: 'Contact saved!',
          severity: 'success',
        });
      } else {
        setAlert({
          message: 'Contact already selected in list.',
          severity: 'error',
        });
      }

      if (currentlySelectedContact !== null && partnerInfo !== null) {
        setContactInfo(currentlySelectedContact);
        const contactIsValid = isContactValid(
          currentlySelectedContact,
          contactConfig,
          selectedProgramId
        );
        setContactError(!contactIsValid);

        await saveApplicationAnswer(
          currentApplication.id,
          question.id,
          contactIsValid ? 'completed' : null,
          'string',
          locale
        );
      }
    }
  }

  async function updateContactInfo(contact: ContactProps) {
    const savedContact = await saveContact(contact, isPreviewMode);

    if (savedContact === null) {
      setAlert({
        message: `${translatedText.common.error}: ${translatedText.contact.messageContactSaveFailed}`,
        severity: 'error',
      });
      return false;
    }

    const relationshipSaved = await saveContactRelationship(savedContact);
    if (!relationshipSaved) {
      return false;
    }

    syncSavedContactAcrossReferences(savedContact);

    const savedAnswer = await saveApplicationAnswer(
      currentApplication.id,
      question.id,
      'completed',
      'string',
      locale
    );

    if (savedAnswer !== null) {
      return true;
    } else {
      setAlert({
        message: `${translatedText.common.error}: ${translatedText.contact.messageContactSaveFailed}`,
        severity: 'error',
      });
      return false;
    }
  }

  async function createNewContact(contact: ContactProps, bypassDuplicateCheck = false) {
    if (!bypassDuplicateCheck) {
      const existingMatch = findExistingNameEmailMatch(contact, applicationContactList);
      if (existingMatch != null) {
        setPendingDuplicateContact({
          draftContact: contact,
          existingContact: existingMatch,
        });
        setDuplicateContactPromptOpen(true);
        return false;
      }
    }

    const savedContact = await saveContact(contact, isPreviewMode);

    if (savedContact === null) {
      setAlert({
        message: `${translatedText.common.error}: ${translatedText.contact.messageContactSaveFailed}`,
        severity: 'error',
      });

      return false;
    }

    const relationshipSaved = await saveContactRelationship(savedContact);
    if (!relationshipSaved) {
      return false;
    }

    setOpenContactModal(false);
    syncSavedContactAcrossReferences(savedContact);
    setContactInfo(savedContact);
    const savedAnswer = await saveApplicationAnswer(
      currentApplication.id,
      question.id,
      'completed',
      'string',
      locale
    );

    if (savedAnswer !== null) {
      return true;
    }

    setAlert({
      message: `${translatedText.common.error}: ${translatedText.contact.messageContactSaveFailed}`,
      severity: 'error',
    });
    return false;
  }

  async function handleContactModalSaved(contact: ContactProps) {
    if (contact.id != null) {
      return await updateContactInfo(contact);
    } else {
      return await createNewContact(contact);
    }
  }

  function closeDuplicateContactPrompt() {
    if (duplicateContactPromptLoading) {
      return;
    }

    setDuplicateContactPromptOpen(false);
    setPendingDuplicateContact(null);
  }

  async function handleUseExistingDuplicateContact() {
    if (pendingDuplicateContact?.existingContact?.id == null) {
      closeDuplicateContactPrompt();
      return;
    }

    setDuplicateContactPromptLoading(true);
    try {
      const relationshipSaved = await saveContactRelationship(
        pendingDuplicateContact.existingContact
      );
      if (!relationshipSaved) {
        return;
      }

      syncSavedContactAcrossReferences(pendingDuplicateContact.existingContact);
      setContactInfo(pendingDuplicateContact.existingContact);
      setOpenContactModal(false);

      const contactIsValid = isContactValid(
        pendingDuplicateContact.existingContact,
        contactConfig,
        selectedProgramId
      );
      setContactError(!contactIsValid);

      await saveApplicationAnswer(
        currentApplication.id,
        question.id,
        contactIsValid ? 'completed' : null,
        'string',
        locale
      );

      setDuplicateContactPromptOpen(false);
      setPendingDuplicateContact(null);
    } finally {
      setDuplicateContactPromptLoading(false);
    }
  }

  async function handleCreateSeparateDuplicateContact() {
    if (pendingDuplicateContact == null) {
      closeDuplicateContactPrompt();
      return;
    }

    setDuplicateContactPromptLoading(true);
    try {
      setDuplicateContactPromptOpen(false);
      const draftContact = pendingDuplicateContact.draftContact;
      setPendingDuplicateContact(null);
      await createNewContact(draftContact, true);
    } finally {
      setDuplicateContactPromptLoading(false);
    }
  }

  function handleContactModalClosed(updatedContact?: ContactProps | null) {
    if (updatedContact !== null && updatedContact !== undefined) {
      setContactInfo(updatedContact);
    } else {
      setContactInfo(initialContact);
    }
    setOpenContactModal(false);
  }

  async function handleDeleteClicked() {
    const cr = (applicationContactList ?? []).filter((c) => {
      return hasRelationshipForType(c, contactConfig.id);
    });
    const targetContact = cr[order];
    if (targetContact?.id == null) {
      setAlert({
        message: translatedText.contact.messageContactSaveFailed,
        severity: 'error',
      });
      throw new Error('No contact relationship found to delete.');
    }

    setLoading(true);
    try {
      const didDelete = await onDelete(targetContact.id);
      if (!didDelete) {
        throw new Error('Delete contact relationship failed.');
      }
    } finally {
      setLoading(false);
    }
  }

  function openDeleteConfirmation() {
    setConfirmDeleteOpen(true);
  }

  function closeDeleteConfirmation() {
    setConfirmDeleteOpen(false);
  }

  const DeleteButton = () => {
    return (
      <Button
        onClick={openDeleteConfirmation}
        sx={{ width: '175px', marginTop: '5px' }}
        variant='secondary'
        size='small'
        disabled={contactError || !canEdit}
      >
        Remove Contact
      </Button>
    );
  };

  const currentTypeContactsCount = (applicationContactList ?? []).filter((contact) =>
    hasRelationshipForType(contact, contactConfig.id)
  ).length;

  const canRemoveContact =
    contactInfo.id !== null &&
    (order > 0 || (contactConfig.maxInstances > 1 && currentTypeContactsCount > 1));

  const contactSelectOptions = useMemo(() => {
    return buildContactSelectOptions(applicationContactList, departmentTypes);
  }, [applicationContactList, departmentTypes]);

  const contactSelectOptionById = useMemo(() => {
    return new Map(contactSelectOptions.map((option) => [option.id, option]));
  }, [contactSelectOptions]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='flex-start'
      className='contact-class'
      gap='20px'
      marginBottom='10px'
      width='100%'
    >
      {openContactModal && (
        <ContactModal
          onClose={handleContactModalClosed}
          contactConfig={contactConfig}
          contact={contactInfo}
          onSave={handleContactModalSaved}
        />
      )}

      <ConfirmationModal
        open={confirmDeleteOpen}
        onClose={closeDeleteConfirmation}
        header='Remove Contact'
        confirmationText='Are you sure you want to remove this contact? This action cannot be undone.'
        confirmButtonText='Remove Contact'
        onDelete={handleDeleteClicked}
      />

      <DuplicateContactPromptModal
        open={duplicateContactPromptOpen}
        isLoading={duplicateContactPromptLoading}
        pendingDuplicateContact={pendingDuplicateContact}
        contactConfig={contactConfig}
        onClose={closeDuplicateContactPrompt}
        onCreateSeparate={handleCreateSeparateDuplicateContact}
        onUseExisting={handleUseExistingDuplicateContact}
      />

      <Box display='flex' flexDirection='column' alignItems='flex-start' width='100%'>
        <Typography style={{ marginTop: '12px', marginBottom: '16px' }} variant='h3'>
          {question.questionText}
          {question.questionNeeded && <RequiredIndicator />}
        </Typography>
        {stringHasValue(question.questionNote) && (
          <Typography style={{ marginBottom: '12px' }}>{question.questionNote}</Typography>
        )}
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box display='flex' flexDirection='column' alignItems='flex-start' gap='20px' width='100%'>
          <Box display='flex' alignItems='left' flexDirection='row' gap='20px' width='80%'>
            <FormControl
              variant='outlined'
              sx={{
                width: '100%',
                marginTop: '-10px',
              }}
              disabled={!canEdit}
            >
              <InputLabel id={question.id.toString()}>
                {translatedText.contact.buttonCreate}
              </InputLabel>
              <Select
                labelId={`contact-select-label=${question.id.toString()}`}
                id={question.id.toString()}
                value={
                  openContactModal && contactInfo?.id == null
                    ? 'new'
                    : (contactInfo?.id?.toString() ?? '')
                }
                renderValue={(value) => {
                  if (value === 'new') {
                    return translatedText.contact.createContact;
                  }

                  const option = contactSelectOptionById.get(value);
                  return option?.selectedLabel ?? '';
                }}
                onChange={handleContactSelect}
              >
                {contactSelectOptions.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id} sx={{ alignItems: 'flex-start' }}>
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      alignItems='flex-start'
                      width='100%'
                      gap='16px'
                    >
                      <Box display='flex' flexDirection='column' minWidth={0}>
                        <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {contact.label}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ lineHeight: 1.2, whiteSpace: 'normal', fontSize: '0.75rem' }}
                        >
                          {contact.detailLine}
                        </Typography>
                        {contact.fallbackDetailLine.length > 0 && (
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ lineHeight: 1.2, whiteSpace: 'normal', fontSize: '0.75rem' }}
                          >
                            {contact.fallbackDetailLine}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ whiteSpace: 'nowrap', marginTop: '2px', fontSize: '0.75rem' }}
                      >
                        {contact.lastUpdatedText.length > 0
                          ? `Updated ${contact.lastUpdatedText}`
                          : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem key='new' value='new'>
                  {translatedText.contact.createContact}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {contactInfo.id !== null && !openContactModal && (
            <Box
              width='80%'
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              gap='20px'
            >
              <ContactCard
                contact={contactInfo}
                contactConfig={contactConfig}
                onEditRequested={() => setOpenContactModal(true)}
              />

              <Box
                display='flex'
                flexGrow={1}
                flexDirection='row'
                flexWrap='wrap'
                justifyContent='flex-start'
                gap={'20px'}
              >
                {contactConfig.maxInstances === 1 && (
                  <Button
                    onClick={() => openBlankContactModal()}
                    sx={{ width: 'auto', marginTop: '5px' }}
                    variant='secondary'
                    size='small'
                    disabled={!canEdit}
                  >
                    {translatedText.contact.buttonChange}
                  </Button>
                )}
                {canRemoveContact ? <DeleteButton /> : ''}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default Contact;
